import { type FC, useState } from "react";
import { type ModalContentsProps } from "@legacy_user_frontend/components/pages/jobs/job_search_navigation/src/search_modal/src/search_modal_props";
import { useProvideModal, ModalContext } from "./src/modules";
import * as styles from "./styles.css";
import { SearchModal } from "./src/search_modal";
import { SearchNavigation } from "./src/search_navigation";

declare const newrelic: any; // eslint-disable-line @typescript-eslint/no-explicit-any

export type JobSearchNavigationProps = {
  gaAction: string;
  dataForNav: Record<
    string,
    {
      [key: string]: string | number | boolean;
      /* 定義しないと代入箇所で怒られるものは個別に定義 */
      id: number;
      search_path: string; // eslint-disable-line camelcase
      is_number_displayed: boolean; // eslint-disable-line camelcase
    }[]
  >;
  searchModalDataFetchUrl: string | null;
  numberOfSearchResultsUrl: string;
};

type Response = {
  searchUrl: string;
  businessTypeContents: {
    name: string;
    occupations: {
      value: string;
      url: string;
      name: string;
      showLink: boolean;
    }[];
  }[];
  prefectureContents: {
    name: string;
    prefecture: {
      name: string;
      url: string;
      value: string;
      showLink: boolean;
    }[];
  }[];
  cityContents: {
    name: string;
    value: string;
    url: string;
    showLink: boolean;
    towns: {
      name: string;
      value: string;
      url: string;
      showLink: boolean;
    }[];
  }[];
  stationContents: {
    lineName: string;
    stations: {
      name: string;
      url: string;
      value: string;
      showLink: boolean;
    }[];
  }[];
};

export const OUTPUT_NAV_NAME: Record<string, string> = {
  prefecture: "都道府県",
  city: "市区町村",
  station: "駅",
  employment: "雇用形態",
  business_type: "職種",
  feature: "こだわり条件",
};

// 検索モーダル（他＋ボタン）が未対応のナビゲーション
const NAV_KEYS_WITHOUT_MORE_SEARCH_BUTTON = ["employment", "feature"];

export const JobSearchNavigation: FC<JobSearchNavigationProps> = ({
  gaAction,
  dataForNav,
  searchModalDataFetchUrl,
  numberOfSearchResultsUrl,
}) => {
  const { isOpen, setIsOpen, modalType, setModalType, openModal, closeModal } = useProvideModal();

  const [modalContents, setModalContents] = useState<ModalContentsProps["modalContents"]>({
    searchUrl: "",
    businessTypeContents: [],
    prefectureContents: [],
    cityContents: [],
    stationContents: [],
  });

  const [isAccordionOpen, setIsAccordionOpen] = useState({});

  const [totalCount, setTotalCount] = useState(0);
  const { cityContents, businessTypeContents, prefectureContents, stationContents } = modalContents;

  // モーダル内のフォームで使用するデータを取得
  if (
    searchModalDataFetchUrl &&
    isOpen &&
    !(
      cityContents?.length ||
      businessTypeContents?.length ||
      prefectureContents?.length ||
      stationContents?.length
    )
  ) {
    (async () => {
      try {
        const res = await fetch(searchModalDataFetchUrl);
        const json: Response = await res.json();
        setModalContents(json);
      } catch (e) {
        if (typeof newrelic !== "undefined") {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          newrelic.noticeError(e);
        }
      }
    })();
  }

  return (
    <ModalContext.Provider
      value={{
        isOpen,
        setIsOpen,
        modalType,
        setModalType,
        openModal,
        closeModal,
      }}
    >
      {isOpen && searchModalDataFetchUrl && (
        <SearchModal
          title={OUTPUT_NAV_NAME[modalType]}
          numberOfSearchResultsUrl={numberOfSearchResultsUrl}
          modalContents={modalContents}
          isAccordionOpen={isAccordionOpen}
          setIsAccordionOpen={setIsAccordionOpen}
          totalCount={totalCount}
          setTotalCount={setTotalCount}
        />
      )}
      <div>
        {Object.entries(dataForNav)
          .map(([key, value]) => ({ key, value }))
          .map((navListGroup) => {
            const moreSearchButtonDisplayed = !!(
              searchModalDataFetchUrl &&
              !NAV_KEYS_WITHOUT_MORE_SEARCH_BUTTON.includes(navListGroup.key)
            );
            return (
              <div key={navListGroup.key} className={styles.searchNavigationContainer}>
                <SearchNavigation
                  navListGroup={navListGroup}
                  gaAction={gaAction}
                  moreSearchButtonDisplayed={moreSearchButtonDisplayed}
                />
              </div>
            );
          })}
      </div>
    </ModalContext.Provider>
  );
};
